import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore'
import { toPublicOrderModeSettings } from '@shared/lib/orderModeSettings'
import type { Settings } from '../settingsStorage'
import { dispatchCatalogEvent, SETTINGS_CHANGED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { stripUndefined } from './stripUndefined'

const settingsCacheByPlace: Record<string, Partial<Settings> | null> = {}
let settingsUnsubscribe: (() => void) | null = null

function settingsDocRef(placeId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'settings', 'app')
}

function publicSettingsDocRef(placeId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'settings', 'public')
}

function dispatchSettingsChanged(): void {
  dispatchCatalogEvent(SETTINGS_CHANGED_EVENT)
}

export async function primeSettingsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (settingsUnsubscribe) {
    settingsUnsubscribe()
    settingsUnsubscribe = null
  }
  const snap = await getDoc(settingsDocRef(placeId))
  settingsCacheByPlace[placeId] = snap.exists() ? (snap.data() as Partial<Settings>) : null
  settingsUnsubscribe = onSnapshot(settingsDocRef(placeId), (docSnap) => {
    settingsCacheByPlace[placeId] = docSnap.exists()
      ? (docSnap.data() as Partial<Settings>)
      : null
    dispatchSettingsChanged()
  })
}

export function getSettingsCache(): Partial<Settings> | null {
  const placeId = getCurrentPlaceId()
  return settingsCacheByPlace[placeId] ?? null
}

export function saveSettings(patch: Partial<Settings>): void {
  const placeId = getCurrentPlaceId()
  const current = settingsCacheByPlace[placeId] ?? null
  settingsCacheByPlace[placeId] = { ...current, ...patch }
  try {
    const payload = stripUndefined(settingsCacheByPlace[placeId]!)
    setDoc(settingsDocRef(placeId), payload, { merge: true }).catch((e) =>
      console.error('Firestore saveSettings failed', e),
    )
    const next = settingsCacheByPlace[placeId]
    if (next?.shared || next?.orderModeSettings) {
      const publicPayload = stripUndefined({
        shared: next.shared,
        orderModeSettings: next.orderModeSettings
          ? toPublicOrderModeSettings(next.orderModeSettings)
          : undefined,
      })
      setDoc(publicSettingsDocRef(placeId), publicPayload, { merge: true }).catch((e) =>
        console.error('Firestore save public settings failed', e),
      )
    }
    dispatchSettingsChanged()
  } catch (e) {
    console.error('Firestore saveSettings rejected invalid data', e)
  }
}

export function isSettingsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in settingsCacheByPlace
}

/** Clear in-memory cache and unsubscribe from real-time listener. */
export function clearSettingsCache(): void {
  if (settingsUnsubscribe) {
    settingsUnsubscribe()
    settingsUnsubscribe = null
  }
  Object.keys(settingsCacheByPlace).forEach((k) => delete settingsCacheByPlace[k])
}
